import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { MathJax } from 'better-react-mathjax';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { chapterSummeryService } from 'src/Services/ApiHandlers/chapterSummeryService';
import { subscribeServices } from 'src/Services/ApiHandlers/subscribeService';
import { Storage } from 'src/Storage';
import { checkAdminRole, checkAuthorOrInstructorRole } from 'src/utils/checkRole';

const marks = [
  {
    value: 0,
    label: 'Short',
  },
  {
    value: 1,
    label: 'Medium',
  },
  {
    value: 2,
    label: 'Long',
  },
];

function valuetext(value) {
  return marks.find((m) => m.value === value).label;
}

const ChapterSummerizeForm = ({
  open,
  setOpen,
  data,
  is_clone,
  is_header,
  book_id,
  chapter_slug,
  is_header_two,
  header_slug,
}) => {
  const hasToken = Storage.get('authToken');
  const hasRole = Storage.get('user-role') && JSON.parse(Storage.get('user-role'));
  const isLoggedIn = hasToken && hasRole;
  const userRole = isLoggedIn?.user_role;

  const [hasSubscribe, setHasSubscribe] = useState(false);
  const [summeryLoading, setSummeryLoading] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [summaryType, setSummaryType] = useState('paragraph');
  const [customLength, setCustomLength] = useState(false);
  const [summaryLength, setSummaryLength] = useState(1);

  const allowRole = useMemo(() => checkAdminRole(userRole) || checkAuthorOrInstructorRole(userRole), [userRole]);

  const checkSubscribe = useCallback(async () => {
    try {
      const response = await subscribeServices.infoApi();
      return response;
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (!allowRole) {
      checkSubscribe()
        .then((response) => {
          if (response.code === 200) {
            setHasSubscribe(response.data.allowAi);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [checkSubscribe, isLoggedIn, allowRole]);

  const shouldShow = allowRole || hasSubscribe;

  const handleClose = () => {
    setOpen(false);
  };

  const handleSummarize = async () => {
    let postData = {
      original_text: data,
      is_clone,
      is_header,
      book_id,
      chapter_slug,
      header_slug,
      is_header_two,
      type: summaryType,
      customLength: !!customLength,
      summaryLength,
    };

    try {
      setSummaryText('');
      setSummeryLoading(true);
      const response = await chapterSummeryService.create(postData);
      if (response.code === 201) {
        toast.success("Chapter's summary created successfully");
        setSummaryText(response.data.summery_text);
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      console.log(err);
      toast.error('Something went wrong!');
    } finally {
      setSummeryLoading(false);
    }
  };

  if (!shouldShow) {
    return (
      <Dialog open={open} maxWidth="xl" onClose={handleClose}>
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          edge="start"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box sx={{ p: '1rem', my: '2rem' }}>
            <Typography variant="h6" align="center">
              To access our summarization feature, please sign in and/or upgrade to the AI Tutor.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} maxWidth="xl" onClose={handleClose}>
      <IconButton
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        edge="start"
        color="inherit"
        onClick={handleClose}
        aria-label="close"
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Box sx={{ p: '1rem', my: '2rem' }}>
          <Stack direction="column" spacing={4}>
            <Stack
              sx={{
                flexDirection: {
                  xs: 'column',
                  md: 'row',
                  alignItems: {
                    xs: 'start',
                    md: 'center',
                  },
                  justifyContent: 'space-between',
                },
              }}
            >
              <Stack
                sx={{
                  flexDirection: {
                    xs: 'column',
                    md: 'row',
                    alignItems: 'center',
                  },
                }}
              >
                <FormControl>
                  <FormLabel>Summary type</FormLabel>
                  <RadioGroup
                    defaultValue="paragraph"
                    onChange={(e) => {
                      setSummaryType(e.target.value);
                    }}
                    row
                    name="row-radio-buttons-group"
                  >
                    <FormControlLabel value="paragraph" control={<Radio />} label="Paragraph" />
                    <FormControlLabel value="bullets" control={<Radio />} label="Bullets Points" />
                  </RadioGroup>
                </FormControl>
                <FormControl>
                  <FormLabel>Custom length</FormLabel>
                  <FormControlLabel
                    control={<Checkbox onChange={(e) => setCustomLength(e.target.checked)} />}
                    label="Custom length"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Summary length</FormLabel>
                  <Slider
                    sx={{ width: '200px', height: '10px', ml: '20px' }}
                    size="medium"
                    aria-label="Restricted values"
                    defaultValue={1}
                    onChange={(value, newValue) => {
                      setSummaryLength(newValue);
                    }}
                    valueLabelFormat={valuetext}
                    getAriaValueText={valuetext}
                    step={null}
                    valueLabelDisplay="auto"
                    max={2}
                    marks={marks}
                    disabled={!customLength}
                  />
                </FormControl>
              </Stack>

              <LoadingButton
                variant="contained"
                color="primary"
                loading={summeryLoading}
                onClick={handleSummarize}
                sx={{ color: 'white', height: '40px' }}
              >
                Summarize
              </LoadingButton>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6} sx={{ p: '10px', maxHeight: '740px', overflowY: 'scroll' }}>
                <MathJax>
                  <div dangerouslySetInnerHTML={{ __html: data }} />
                </MathJax>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: '5px',
                  p: '10px',
                  maxHeight: '740px',
                  overflowY: 'scroll',
                }}
              >
                <MathJax dynamic>
                  <div dangerouslySetInnerHTML={{ __html: summaryText }} />
                </MathJax>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChapterSummerizeForm;
